/**
 * Local assessments (Phase 16C C2). Encrypted store `assessments`, key = assessmentId.
 * Autosave writes here; the outbox keeps only the last pending update per assessment.
 */
import type { OfflineRuntime } from '../runtime';
import type { SyncState } from './visitRepo';

export interface LocalAssessment {
  assessmentId: string;
  visitId: string;
  patientId: string;
  /** The assessment body as the online endpoint takes it */
  body: Record<string, unknown>;
  status: 'DRAFT' | 'COMPLETED';
  serverVersion?: number;
  syncState: SyncState;
  origin: 'local' | 'server';
  createdAt: string;
  updatedAt: string;
}

export class AssessmentRepo {
  constructor(private readonly rt: OfflineRuntime) {}

  private stamp(): string { return new Date(this.rt.now()).toISOString(); }

  get(assessmentId: string): Promise<LocalAssessment | undefined> {
    return this.rt.store().get<LocalAssessment>('assessments', assessmentId);
  }

  async put(a: LocalAssessment): Promise<LocalAssessment> {
    const next = { ...a, updatedAt: this.stamp() };
    await this.rt.store().put('assessments', a.assessmentId, next);
    return next;
  }

  async createLocal(assessmentId: string, visitId: string, patientId: string, body: Record<string, unknown>): Promise<LocalAssessment> {
    const now = this.stamp();
    return this.put({ assessmentId, visitId, patientId, body, status: 'DRAFT', syncState: 'pending', origin: 'local', createdAt: now, updatedAt: now });
  }

  async updateLocal(assessmentId: string, body: Record<string, unknown>): Promise<LocalAssessment> {
    const a = await this.get(assessmentId);
    if (!a) throw new Error('Assessment not found on this device');
    return this.put({ ...a, body: { ...a.body, ...body }, syncState: 'pending' });
  }

  async completeLocal(assessmentId: string): Promise<LocalAssessment> {
    const a = await this.get(assessmentId);
    if (!a) throw new Error('Assessment not found on this device');
    return this.put({ ...a, status: 'COMPLETED', syncState: 'pending' });
  }

  async listForVisit(visitId: string): Promise<LocalAssessment[]> {
    return (await this.rt.store().getAll<LocalAssessment>('assessments')).map((r) => r.value).filter((a) => a.visitId === visitId);
  }

  async listForPatient(patientId: string): Promise<LocalAssessment[]> {
    return (await this.rt.store().getAll<LocalAssessment>('assessments')).map((r) => r.value).filter((a) => a.patientId === patientId);
  }

  async markSynced(assessmentId: string, version?: number): Promise<void> {
    const a = await this.get(assessmentId);
    if (a) await this.put({ ...a, serverVersion: version ?? a.serverVersion, syncState: 'synced' });
  }

  async setSyncState(assessmentId: string, syncState: SyncState): Promise<void> {
    const a = await this.get(assessmentId);
    if (a) await this.put({ ...a, syncState });
  }
}
